import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import { formatDate, formatDateTime } from '../utils/helpers';

// ─── Constantes ───────────────────────────────────────────────────────────────
const OVERTIME_PICKUP   = 30; // minutes
const OVERTIME_DELIVERY = 45;

// ─── Utilitaire : formatage durée ────────────────────────────────────────────
const fmtDuration = (min) => {
  if (!min && min !== 0) return '—';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h${min % 60 > 0 ? ` ${min % 60}min` : ''}`;
};

// ─── Composant : carte course active ─────────────────────────────────────────
const LiveCourseCard = ({ course, onViewOrder }) => {
  const isPickup   = course.course_type === 'pickup';
  const overtime   = course.elapsed_minutes > (isPickup ? OVERTIME_PICKUP : OVERTIME_DELIVERY);
  const address    = isPickup ? course.pickup_address : course.delivery_address;

  return (
    <div className="card" style={{
      borderLeft: `3px solid ${overtime ? 'var(--red)' : isPickup ? 'var(--orange)' : 'var(--green)'}`,
      padding: 16,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge className={isPickup ? 'badge-orange' : 'badge-green'}>
            {isPickup ? '📦 Pickup' : '🚚 Delivery'}
          </Badge>
          {overtime && <Badge className="badge-red">⚠️ Retard</Badge>}
        </div>
        <div style={{
          fontSize: 20, fontWeight: 800, fontFamily: 'Syne',
          color: overtime ? 'var(--red)' : 'var(--text2)',
        }}>
          {course.elapsed_minutes} min
        </div>
      </div>

      {/* Infos */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 14 }}>
        {[
          { icon: '🔖', label: 'Commande', val: (
            <button
              className="btn btn-ghost btn-sm"
              style={{ padding: '2px 6px', fontSize: 12 }}
              onClick={() => onViewOrder(course.order_id)}
            >
              {course.order_number}
            </button>
          )},
          { icon: '👤', label: 'Livreur',  val: course.livreur_name  },
          { icon: '📞', label: 'Tél.',     val: (
            <a href={`tel:${course.livreur_phone}`} style={{ color: 'var(--accent)' }}>
              {course.livreur_phone}
            </a>
          )},
          { icon: '📍', label: 'Dest.',    val: address || '—' },
        ].map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 14, minWidth: 20, marginTop: 1 }}>{r.icon}</span>
            <span style={{ fontSize: 11, color: 'var(--text3)', minWidth: 50 }}>{r.label}</span>
            <span style={{ fontSize: 12, color: 'var(--text)', flex: 1, lineHeight: 1.4 }}>{r.val}</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        {course.destination_lat && course.destination_lng && (
          <a
            href={`https://maps.google.com/?q=${course.destination_lat},${course.destination_lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary btn-sm"
            style={{ flex: 1, justifyContent: 'center' }}
          >
            📍 Carte
          </a>
        )}
        <a
          href={`tel:${course.livreur_phone}`}
          className="btn btn-ghost btn-sm"
          style={{ flex: 1, justifyContent: 'center' }}
        >
          📞 Appeler
        </a>
      </div>
    </div>
  );
};

// ─── Panneau : Monitoring temps réel ─────────────────────────────────────────
const MonitoringPanel = () => {
  const navigate = useNavigate();
  const [courses,      setCourses]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [autoRefresh,  setAutoRefresh]  = useState(true);
  const [interval,     setIntervalSec]  = useState(30);
  const [lastRefresh,  setLastRefresh]  = useState(null);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await adminApi.getActiveCourses();
      setCourses(res.data || []);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchCourses, interval * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, interval, fetchCourses]);

  const pickups   = useMemo(() => courses.filter(c => c.course_type === 'pickup'),   [courses]);
  const deliveries= useMemo(() => courses.filter(c => c.course_type === 'delivery'), [courses]);
  const overtimes = useMemo(() => courses.filter(c =>
    c.elapsed_minutes > (c.course_type === 'pickup' ? OVERTIME_PICKUP : OVERTIME_DELIVERY)
  ), [courses]);

  return (
    <div>
      {/* Barre de contrôle */}
      <div className="filter-bar" style={{ marginBottom: 20 }}>
        <button className="btn btn-secondary btn-sm" onClick={fetchCourses}>
          ↺ Rafraîchir
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: 'var(--text2)' }}>Auto-refresh</label>
          <button
            className={`btn btn-sm ${autoRefresh ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setAutoRefresh(v => !v)}
          >
            {autoRefresh ? '⏸ Pause' : '▶ Actif'}
          </button>
          {autoRefresh && (
            <select
              className="filter-select"
              value={interval}
              onChange={e => setIntervalSec(Number(e.target.value))}
            >
              <option value={15}>15s</option>
              <option value={30}>30s</option>
              <option value={60}>1 min</option>
              <option value={120}>2 min</option>
            </select>
          )}
        </div>
        {lastRefresh && (
          <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>
            Mis à jour : {lastRefresh.toLocaleTimeString('fr-FR')}
          </span>
        )}
      </div>

      {/* Stats bar */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { icon: '⚡', label: 'Courses actives',  val: courses.length,    color: 'var(--accent)' },
          { icon: '📦', label: 'Pickups en cours', val: pickups.length,    color: 'var(--orange)' },
          { icon: '🚚', label: 'Deliveries',        val: deliveries.length, color: 'var(--green)'  },
          { icon: '⚠️', label: 'En retard',          val: overtimes.length,  color: overtimes.length > 0 ? 'var(--red)' : 'var(--text3)' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-card-accent" style={{ background: s.color }} />
            <div className="stat-icon" style={{ background: 'var(--bg3)', fontSize: 18 }}>{s.icon}</div>
            <div className="stat-value" style={{ color: s.color }}>{s.val}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? <Loading text="Chargement des courses..." />
        : error ? <ApiError message={error} onRetry={fetchCourses} />
        : courses.length === 0
          ? <EmptyState icon="🚗" title="Aucune course en cours" text="Toutes les livraisons sont terminées pour le moment." />
          : (
            <>
              {/* Retards en alerte */}
              {overtimes.length > 0 && (
                <div className="inline-alert inline-alert-red" style={{ marginBottom: 16 }}>
                  <span>⚠️</span>
                  <div>
                    <strong>{overtimes.length} course(s) en retard</strong> — durée dépassée (pickup &gt;{OVERTIME_PICKUP}min, delivery &gt;{OVERTIME_DELIVERY}min).
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                {courses
                  .sort((a, b) => {
                    // retards d'abord, puis par durée décroissante
                    const aOver = a.elapsed_minutes > (a.course_type === 'pickup' ? OVERTIME_PICKUP : OVERTIME_DELIVERY);
                    const bOver = b.elapsed_minutes > (b.course_type === 'pickup' ? OVERTIME_PICKUP : OVERTIME_DELIVERY);
                    if (aOver !== bOver) return aOver ? -1 : 1;
                    return b.elapsed_minutes - a.elapsed_minutes;
                  })
                  .map(c => (
                    <LiveCourseCard
                      key={c.course_id}
                      course={c}
                      onViewOrder={(id) => navigate(`/commandes?order=${id}`)}
                    />
                  ))
                }
              </div>
            </>
          )
      }
    </div>
  );
};

// ─── Panneau : Rapport performance QR codes ──────────────────────────────────
const QRReportPanel = () => {
  const toast = useToast();
  // Dates par défaut : 30 derniers jours
  const today30 = new Date(); today30.setDate(today30.getDate() - 30);
  const [startDate, setStartDate] = useState(today30.toISOString().slice(0, 10));
  const [endDate,   setEndDate]   = useState(new Date().toISOString().slice(0, 10));
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await adminApi.getQRPerformanceReport({ start_date: startDate, end_date: endDate });
      setData(res.data);
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const successRate = data?.summary ? {
    qr1: data.summary.total_orders > 0 ? ((data.summary.qr1_scanned / data.summary.total_orders) * 100).toFixed(1) : '0.0',
    qr3: data.summary.total_orders > 0 ? ((data.summary.qr3_scanned / data.summary.total_orders) * 100).toFixed(1) : '0.0',
    qr4: data.summary.total_orders > 0 ? ((data.summary.qr4_scanned / data.summary.total_orders) * 100).toFixed(1) : '0.0',
  } : null;

  return (
    <div>
      {/* Filtres dates */}
      <div className="filter-bar" style={{ marginBottom: 20 }}>
        <div className="form-row" style={{ gap: 12, flexWrap: 'wrap', margin: 0 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date début</label>
            <input type="date" className="form-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date fin</label>
            <input type="date" className="form-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end' }} onClick={fetchReport}>
            Appliquer
          </button>
        </div>
        {/* Raccourcis */}
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {[
            { label: '7j',  days: 7  },
            { label: '30j', days: 30 },
            { label: '90j', days: 90 },
          ].map(s => {
            const d = new Date(); d.setDate(d.getDate() - s.days);
            return (
              <button
                key={s.label}
                className="btn btn-ghost btn-sm"
                onClick={() => { setStartDate(d.toISOString().slice(0, 10)); setEndDate(new Date().toISOString().slice(0, 10)); }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? <Loading text="Chargement du rapport..." />
        : error ? <ApiError message={error} onRetry={fetchReport} />
        : !data ? null
        : (
          <>
            {/* KPIs globaux */}
            <div className="stats-grid" style={{ marginBottom: 20 }}>
              {[
                { icon: '📦', label: 'Total commandes', val: data.summary.total_orders,             color: 'var(--text)'   },
                { icon: '🔵', label: 'QR1 — taux scan', val: `${successRate.qr1}%`,                 color: 'var(--blue)'   },
                { icon: '🟠', label: 'QR3 — taux scan', val: `${successRate.qr3}%`,                 color: 'var(--orange)' },
                { icon: '🟢', label: 'QR4 — taux scan', val: `${successRate.qr4}%`,                 color: 'var(--green)'  },
              ].map((s, i) => (
                <div key={i} className="stat-card">
                  <div className="stat-card-accent" style={{ background: s.color }} />
                  <div className="stat-icon" style={{ background: 'var(--bg3)', fontSize: 18 }}>{s.icon}</div>
                  <div className="stat-value" style={{ color: s.color }}>{s.val}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Temps moyens */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header">
                <div className="card-title">⏱️ Temps moyens par étape</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { label: 'Création commande → QR1 scanné',     val: data.summary.avg_time_to_qr1, max: 60,  color: 'var(--blue)'   },
                  { label: 'Réception laverie → QR3 scanné',      val: data.summary.avg_time_to_qr3, max: 30,  color: 'var(--orange)' },
                  { label: 'QR3 scanné → QR4 (livraison client)', val: data.summary.avg_time_to_qr4, max: 60,  color: 'var(--green)'  },
                ].map((row, i) => {
                  const mins = row.val ? Math.round(row.val) : null;
                  const pct  = mins ? Math.min(100, Math.round((mins / row.max) * 100)) : 0;
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: 'var(--text2)' }}>{row.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: row.color }}>
                          {mins !== null ? fmtDuration(mins) : '—'}
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${pct}%`, background: row.color, transition: 'width 0.5s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tableau jour par jour */}
            {data.daily?.length > 0 && (
              <div className="card" style={{ padding: 0 }}>
                <div className="card-header" style={{ padding: '16px 20px 12px' }}>
                  <div className="card-title">📅 Évolution quotidienne</div>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>{data.daily.length} jours</span>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th style={{ color: 'var(--text2)' }}>Commandes</th>
                        <th style={{ color: 'var(--blue)' }}>QR1</th>
                        <th style={{ color: 'var(--orange)' }}>QR3</th>
                        <th style={{ color: 'var(--green)' }}>QR4</th>
                        <th>Taux global</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.daily].reverse().map((row, i) => {
                        const globalRate = row.orders > 0
                          ? (((row.qr1 + row.qr3 + row.qr4) / (row.orders * 3)) * 100).toFixed(0)
                          : 0;
                        return (
                          <tr key={i}>
                            <td className="td-main">{formatDate(row.date)}</td>
                            <td>{row.orders}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: 'var(--blue)', fontWeight: 600 }}>{row.qr1}</span>
                                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                                  {row.orders > 0 ? `${Math.round((row.qr1 / row.orders) * 100)}%` : '—'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: 'var(--orange)', fontWeight: 600 }}>{row.qr3}</span>
                                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                                  {row.orders > 0 ? `${Math.round((row.qr3 / row.orders) * 100)}%` : '—'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: 'var(--green)', fontWeight: 600 }}>{row.qr4}</span>
                                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                                  {row.orders > 0 ? `${Math.round((row.qr4 / row.orders) * 100)}%` : '—'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div className="progress-bar" style={{ flex: 1, height: 6 }}>
                                  <div className="progress-fill" style={{
                                    width: `${globalRate}%`,
                                    background: globalRate >= 80 ? 'var(--green)' : globalRate >= 50 ? 'var(--orange)' : 'var(--red)',
                                  }} />
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 600, minWidth: 32, color: 'var(--text)' }}>{globalRate}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )
      }
    </div>
  );
};

// ─── Page principale : Courses ────────────────────────────────────────────────
export const Courses = () => {
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab === 'qr' ? 'qr' : 'monitoring');

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 24, marginBottom: 6 }}>🚗 Courses & QR Codes</div>
        <div style={{ color: 'var(--text2)', fontSize: 13 }}>Monitoring des livraisons en temps réel et analyse des QR codes</div>
      </div>

      {/* TABS */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 4, width: 'fit-content' }}>
        {[
          { key: 'monitoring', label: '⚡ Monitoring temps réel' },
          { key: 'qr',         label: '📲 Rapport QR Codes' },
        ].map(t => (
          <button
            key={t.key}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.key)}
            style={{ borderRadius: 9 }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'monitoring' && <MonitoringPanel />}
      {tab === 'qr'         && <QRReportPanel  />}
    </div>
  );
};